import React, { useEffect, useState } from 'react';

interface IterationVisualizationSVGProps {
  /**
   * Не используется для отрисовки: точки берутся из `iterationResults`
   * по `metricKey`, потому что только там есть `seq` для нулевой точки и
   * дедупликации. Проп остаётся ради вызывающих, которые его ещё передают.
   */
  iterationData: number[];
  iterations: number;
  graphSliderValue: number;
  baseColor: string;
  optimizationProgress?: number;
  iterationResults?: Array<{
    prompt: string;
    embedding_point?: { x: number; y: number };
    evaluation_point?: { x: number; y: number };
    quality_point?: { x: number; y: number };
    actor_tokens_point?: { x: number; y: number };
    iteration: number;
    seq?: number; // seq from backend: 0 = baseline evaluation, 1+ = optimized iterations
  }>;
  isMultiAgent?: boolean;
  /** Какое поле точки на iterationResults рисовать. По умолчанию — evaluation_point (метрика отбора). */
  metricKey?: 'evaluation_point' | 'quality_point' | 'actor_tokens_point';
  /** Подпись оси Y — по умолчанию "Evaluation", для графиков качества/токенов задаётся явно. */
  yAxisLabel?: string;
}

const IterationVisualizationSVG: React.FC<IterationVisualizationSVGProps> = ({
  iterationData,
  iterations,
  graphSliderValue,
  baseColor,
  optimizationProgress = 0,
  iterationResults,
  isMultiAgent = false,
  metricKey = 'evaluation_point',
  yAxisLabel = 'Оценка',
}) => {

  const [dimensions, setDimensions] = useState(() => {
    if (typeof window !== 'undefined') {
      const width = window.innerWidth;
      if (width >= 1200) {
        return { width: 450, height: 350, padding: 60 };
      } else if (width >= 768) {
        return { width: 400, height: 300, padding: 50 };
      } else if (width >= 480) {
        return { width: 350, height: 250, padding: 50 };
      } else {
        return { width: 300, height: 200, padding: 50 };
      }
    }
    return { width: 400, height: 300, padding: 50 };
  });

  // Обновляем размеры при изменении размера окна
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width >= 1200) {
        setDimensions({ width: 450, height: 350, padding: 60 });
      } else if (width >= 768) {
        setDimensions({ width: 400, height: 300, padding: 50 });
      } else if (width >= 480) {
        setDimensions({ width: 350, height: 250, padding: 50 });
      } else {
        setDimensions({ width: 300, height: 200, padding: 50 });
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const { width, height, padding } = dimensions;
  const chartWidth = width - 2 * padding;
  const chartHeight = height - 2 * padding;
  const originX = padding;
  const originY = height - padding;

  // Показываем точки только на основе реальных данных от бэкенда
  const maxVisiblePoints = iterationResults ? iterationResults.length : 0;
  // Вычисляем общее количество ожидаемых точек для нормализации оси X
  const totalPoints = iterations + 1; // +1 для первого ответа

  // Защита от NaN в iterationData
  const safeIterationData = Array.isArray(iterationData)
    ? iterationData.map(x => isNaN(Number(x)) ? 0 : Number(x))
    : [];

  // Generate points for a wavy/flowing effect
  const generateFlowingPoints = (numVisiblePoints: number) => {
    const points = [];
    
    // Если есть данные с бэкенда, используем их для расчета координат
    if (iterationResults && iterationResults.length > 0) {
      // Показываем только реальные точки из бэкенда и сортируем по seq/iteration
      const visibleResults = [...iterationResults].sort((a, b) => {
        const ai = a.seq !== undefined ? a.seq : (a.iteration !== undefined ? a.iteration : 0);
        const bi = b.seq !== undefined ? b.seq : (b.iteration !== undefined ? b.iteration : 0);
        return ai - bi;
      });
      
      // Находим диапазон значений для оси Y (значение по metricKey)
      const evaluationYValues = visibleResults
        .filter(r => r[metricKey])
        .map(r => r[metricKey]!.y);

      let minY = 0, maxY = 1;
      if (evaluationYValues.length > 0) {
        minY = Math.min(...evaluationYValues);
        maxY = Math.max(...evaluationYValues);
        // Добавляем небольшой отступ сверху и снизу
        const padding = (maxY - minY) * 0.1;
        minY = Math.max(0, minY - padding);
        maxY = maxY + padding;
      }
      
      // Определяем максимальное количество итераций для отображения
      let maxIteration = iterations;
      if (iterationResults && iterationResults.length > 0) {
        // Находим максимальный seq или iteration из результатов
        const maxSeq = Math.max(...iterationResults
          .filter(r => r.seq !== undefined || r.iteration !== undefined)
          .map(r => r.seq !== undefined ? r.seq : (r.iteration !== undefined ? r.iteration : 0))
        );
        maxIteration = Math.max(maxIteration, maxSeq);
      }
      
      const yRange = maxY - minY;
      
      for (let i = 0; i < visibleResults.length; i++) {
        const result = visibleResults[i];
        if (result && result[metricKey]) {
          // Используем seq или iteration для правильной нумерации
          // seq: 0 -> iterationNumber: 0, seq: 1 -> iterationNumber: 1, etc.
          const iterationNumber = result.seq !== undefined ? result.seq : (result.iteration !== undefined ? result.iteration : (isMultiAgent ? i + 1 : i));

          // Позиция X: используем iterationNumber для правильного позиционирования
          // seq: 0 -> x=0, seq: 1 -> x=1, etc., используя maxIteration для нормализации
          const normalizedX = maxIteration > 0 ? iterationNumber / maxIteration : 0;
          const x = padding + normalizedX * chartWidth;

          // Ось Y: значение по metricKey, нормализованное от minY до maxY
          const normalizedY = yRange > 0 ? (result[metricKey]!.y - minY) / yRange : 0.5;
          
          const y = originY - normalizedY * chartHeight; // Инвертируем Y-ось
          
          // Ограничиваем координаты границами графика
          points.push({ 
            x: Math.max(padding, Math.min(width - padding, x)),
            y: Math.max(padding, Math.min(height - padding, y)),
            originalIndex: i,
            iterationNumber: iterationNumber
          });
        } else {
          // Если нет evaluation_point — пропускаем точку (не рисуем синтетические точки)
          continue;
        }
      }
    } else {
      // Если нет данных с бэкенда, не показываем точки
      return [];
    }
    
    return points;
  };

  // Generate only visible points based on optimization progress
  const visibleIterationPoints = generateFlowingPoints(maxVisiblePoints);

  // Create SVG path data for the line and the filled area (only for visible points)
  const linePathData = visibleIterationPoints
    .map((p, index) => `${index === 0 ? 'M' : 'L'} ${p.x},${p.y}`)
    .join(' ');
  
  // Debug: log the path data and points
  console.log('🔍 IterationVisualizationSVG debug:', {
    visibleIterationPointsCount: visibleIterationPoints.length,
    linePathData: linePathData,
    points: visibleIterationPoints.map(p => ({ x: p.x, y: p.y, iteration: p.iterationNumber }))
  });
  
  // отключаем общую заливку, чтобы не соединять несмежные точки визуально
  let filledPathData = '';

  // Create separate path segments for different colors (only for visible points)
  const createColoredPathSegments = () => {
    const segments = [];
    for (let i = 0; i < visibleIterationPoints.length - 1; i++) {
      const currentPoint = visibleIterationPoints[i];
      const nextPoint = visibleIterationPoints[i + 1];
      
      // Логика подсветки сегментов: учитываем точку 0 (первый ответ) и оптимизированные промпты
      // graphSliderValue указывает на выбранную итерацию оптимизированного промпта (0-3 для 4 итераций)
      // Сегмент подсвечивается, если следующая точка должна быть подсвечена
      const currentIterationNumber = currentPoint.iterationNumber !== undefined ? currentPoint.iterationNumber : i;
      const nextIterationNumber = nextPoint.iterationNumber !== undefined ? nextPoint.iterationNumber : i + 1;

      // Исправление: всегда соединяем соседние видимые точки,
      // даже если между ними пропущены номера итераций.
      const isSegmentHighlighted = currentIterationNumber === 0 || (nextIterationNumber > 0 && nextIterationNumber <= graphSliderValue + 1);
      
      segments.push({
        pathData: `M${currentPoint.x},${currentPoint.y} L${nextPoint.x},${nextPoint.y}`,
        color: isSegmentHighlighted ? '#FF8300' : baseColor
      });
    }
    return segments;
  };

  const pathSegments = createColoredPathSegments();

  // Define gradient for depth effect (falling gradient)
  const gradientId = `iterationGradient-${Math.random().toString(36).substring(7)}`;
  // Базовая толщина линии для сплошной линии и сегментов
  const baseStrokeWidth = width >= 1200 ? 4 : width >= 768 ? 3 : width >= 480 ? 2.5 : 2;

  return (
    <svg 
      width="100%" 
      height="100%" 
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="xMidYMid meet"
      style={{ 
        maxWidth: '100%', 
        height: 'auto',
        minHeight: '250px'
      }}
    >
      <defs>
        {/* Gradient for falling effect: from top (opaque) to bottom (transparent) */}
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={baseColor + 'FF'} /> 
          <stop offset="100%" stopColor={baseColor + '00'} />
        </linearGradient>

        {/* Drop shadow filter */}
        <filter id="dropShadow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur in="SourceAlpha" stdDeviation="3" />
          <feOffset dx="2" dy="2" result="offsetblur" />
          <feComponentTransfer>
            <feFuncA type="linear" slope="0.5"/>
          </feComponentTransfer>
          <feMerge>
            <feMergeNode/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>

      {/* Axes */}
      <line 
        x1={padding} 
        y1={originY} 
        x2={width - padding} 
        y2={originY} 
        stroke="#0A2A47" 
        strokeWidth={width >= 480 ? 1 : 0.5} 
      />
      <line 
        x1={padding} 
        y1={originY} 
        x2={padding} 
        y2={padding} 
        stroke="#0A2A47" 
        strokeWidth={width >= 480 ? 1 : 0.5} 
      />

      {/* X-axis ticks (без подписей - подписи только под точками) */}
      {(() => {
        const ticks = [];
        
        // Определяем максимальное количество итераций для отображения
        let maxIteration = iterations;
        if (iterationResults && iterationResults.length > 0) {
          // Находим максимальный seq или iteration из результатов
          const maxSeq = Math.max(...iterationResults
            .filter(r => r.seq !== undefined || r.iteration !== undefined)
            .map(r => r.seq !== undefined ? r.seq : (r.iteration !== undefined ? r.iteration : 0))
          );
          maxIteration = Math.max(maxIteration, maxSeq);
        }
        
        // Показываем только тики (вертикальные линии) без подписей
        for (let i = 0; i <= maxIteration; i++) {
          // Позиция X для каждой точки (как в generateFlowingPoints)
          const iterationNumber = i;
          const normalizedX = maxIteration > 0 ? iterationNumber / maxIteration : 0;
          const x = padding + normalizedX * chartWidth;
          
          ticks.push(
            <line 
              key={`x-tick-${i}`}
              x1={x} 
              y1={originY} 
              x2={x} 
              y2={originY + (width >= 480 ? 5 : 3)} 
              stroke="#0A2A47" 
              strokeWidth={width >= 480 ? 1 : 0.5} 
            />
          );
        }
        return ticks;
      })()}

      {/* Y-axis ticks and labels */}
      {(() => {
        const ticks = [];
        const maxTicks = 4;
        
        // Находим диапазон значений для оси Y
        let minY = 0, maxY = 1;
        if (iterationResults && iterationResults.length > 0) {
          const evaluationYValues = iterationResults
            .filter(r => r[metricKey])
            .map(r => r[metricKey]!.y);
          if (evaluationYValues.length > 0) {
            minY = Math.min(...evaluationYValues);
            maxY = Math.max(...evaluationYValues);
            // Добавляем небольшой отступ сверху
            const padding = (maxY - minY) * 0.1;
            minY = Math.max(0, minY - padding);
            maxY = maxY + padding;
          }
        }
        
        for (let i = 0; i <= maxTicks; i++) {
          const y = originY - (chartHeight * i) / maxTicks;
          
          // Рассчитываем значение на основе реального диапазона, начиная с 0
          const value = minY + (maxY - minY) * (i / maxTicks);
          
          ticks.push(
            <React.Fragment key={`y-tick-${i}`}>
              <line 
                x1={originX} 
                y1={y} 
                x2={originX - (width >= 480 ? 5 : 3)} 
                y2={y} 
                stroke="#0A2A47" 
                strokeWidth={width >= 480 ? 1 : 0.5} 
              />
              <text 
                x={originX - (width >= 1200 ? 20 : width >= 768 ? 18 : width >= 480 ? 15 : 12)} 
                y={y + (width >= 1200 ? 4 : width >= 768 ? 3 : width >= 480 ? 2 : 1)} 
                textAnchor="end" 
                fill="#0A2A47" 
                fontSize={width >= 1200 ? 14 : width >= 768 ? 12 : width >= 480 ? 11 : 10}
                fontWeight="600"
              >
                {typeof value === 'number' ? value.toFixed(2) : value}
              </text>
            </React.Fragment>
          );
        }
        return ticks;
      })()}

      {/* X-axis label */}
      <text 
        x={width / 2} 
        y={height - padding / 8} 
        textAnchor="middle" 
        fill="#0A2A47" 
        fontSize={width >= 1200 ? 18 : width >= 768 ? 16 : width >= 480 ? 14 : 12}
        fontWeight="500"
      >
        Итерация
      </text>
      {/* Y-axis label */}
      <text 
        x={padding / 6} 
        y={height / 2} 
        textAnchor="middle" 
        transform={`rotate(-90 ${padding / 6} ${height / 2})`} 
        fill="#0A2A47" 
        fontSize={width >= 1200 ? 18 : width >= 768 ? 16 : width >= 480 ? 14 : 12}
        fontWeight="500"
      >
        {yAxisLabel}
      </text>

      {/* Main flowing path - filled with depth gradient and shadow */}
      {visibleIterationPoints.length > 0 && (
        <path
          d={filledPathData} // Use filledPathData for the filled area
          fill={`url(#${gradientId})`}
          stroke="none" // No stroke for the filled area
          filter="url(#dropShadow)" // Apply the drop shadow filter here
          style={{ 
            animation: 'fadeInFill 1s ease-out 0.2s both'
          }}
        />
      )}

      {/* Removed separate base line to avoid double-drawing over colored segments */}

      {/* Сплошная линия, соединяющая все видимые точки (гарантирует непрерывность) */}
      {visibleIterationPoints.length > 1 && linePathData && (
        <path
          d={linePathData}
          fill="none"
          stroke={baseColor}
          strokeWidth={baseStrokeWidth}
          filter="url(#dropShadow)"
          style={{ opacity: 0.9 }}
        />
      )}

      {/* Colored line segments */}
      {pathSegments.map((segment, index) => {
        // Адаптивные размеры для линий
        const strokeWidth = baseStrokeWidth;
        
        return (
          <path
            key={index}
            d={segment.pathData}
            fill="none"
            stroke={segment.color}
            strokeWidth={strokeWidth}
            filter="url(#dropShadow)"
            style={{ 
              transition: 'stroke 0.3s ease-in-out',
              animation: `drawLine 0.5s ease-out ${index * 0.1}s both`
            }}
          />
        );
      })}

      {/* Points with highlighting */}
      {visibleIterationPoints.map((point, index) => {
        // Адаптивные размеры для точек
        const pointRadius = width >= 1200 ? 6 : width >= 768 ? 5 : width >= 480 ? 4 : 3;
        
        // Логика подсветки: учитываем точку 0 (первый ответ) и оптимизированные промпты
        // graphSliderValue указывает на выбранную итерацию оптимизированного промпта (0-3 для 4 итераций)
        // Точка 0 всегда подсвечена, остальные точки подсвечиваются в зависимости от выбранной итерации
        const iterationNumber = point.iterationNumber !== undefined ? point.iterationNumber : index;
        const isHighlighted = iterationNumber === 0 || (iterationNumber > 0 && iterationNumber <= graphSliderValue + 1);
        
        return (
          <circle
            key={`point-${index}`}
            cx={point.x}
            cy={point.y}
            r={pointRadius}
            fill={isHighlighted ? '#FF8300' : baseColor}
            stroke={isHighlighted ? '#FF8300' : baseColor}
            strokeWidth="2"
            style={{ 
              transition: 'all 0.3s ease-in-out',
              animation: `fadeInScale 0.6s ease-out ${index * 0.15}s both`
            }}
          />
        );
      })}

      {/* Метки под точками с номерами итераций - рендерятся последними чтобы быть поверх всех элементов */}
      {visibleIterationPoints.map((point, index) => {
        const labelFontSize = width >= 1200 ? 14 : width >= 768 ? 12 : width >= 480 ? 11 : 10;
        const iterationNumber = point.iterationNumber !== undefined ? point.iterationNumber : index;
        
        // Позиция метки: немного ниже оси X (originY = height - padding)
        // Метки должны быть между осью X и подписью "Итерация"
        // Подпись оси X находится на height - padding/8
        // originY = height - padding, поэтому метки на originY + 20-22px будут видны
        // Для мобильных устройств используем меньший отступ
        const labelY = originY + (width >= 480 ? 18 : 14);
        
        // Убеждаемся, что метки не выходят за пределы SVG и не перекрываются с подписью оси X
        // Подпись оси X находится на height - padding/8, поэтому метки должны быть выше
        const maxLabelY = height - padding / 4;
        const finalLabelY = Math.min(labelY, maxLabelY);
        
        // Если метка все еще может быть видна, рендерим её
        if (finalLabelY <= originY || finalLabelY >= height) {
          return null; // Не рендерим метку если она выходит за пределы
        }
        
        return (
          <text
            key={`label-${index}`}
            x={point.x}
            y={finalLabelY}
            fontSize={labelFontSize}
            fill="#0A2A47"
            fontWeight="600"
            textAnchor="middle"
            dominantBaseline="hanging"
            style={{ 
              transition: 'all 0.3s ease-in-out',
              animation: `fadeInText 0.4s ease-out ${index * 0.15 + 0.2}s both`,
              pointerEvents: 'none',
              userSelect: 'none'
            }}
          >
            {iterationNumber}
          </text>
        );
      })}

      <style>
        {`
          @keyframes drawLine {
            from {
              stroke-dasharray: 1000;
              stroke-dashoffset: 1000;
            }
            to {
              stroke-dashoffset: 0;
            }
          }
          
          @keyframes fadeInScale {
            from {
              opacity: 0;
              transform: scale(0);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }
          
          @keyframes fadeInText {
            from {
              opacity: 0;
              transform: translateY(-5px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes fadeInFill {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }
        `}
      </style>
    </svg>
  );
};

export default IterationVisualizationSVG; 